import { Component, OnInit, OnDestroy, Inject, HostListener } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { LibConfigService, LibConfig } from '../vaelsys-webrtc-lib.module';
import { CallError, CallConnectionState, VaelsysWebrtcLibService, VaelsysWebrtcComponent } from '../vaelsys-webrtc-lib.service'; 

// Import the js classes and objects we will need
import {DeviceWebRtcCall} from "./device_webrtc_call";
import { wamp_call } from "../wamp_client";

export enum PTZCommand {
  stop = 0,
  up = 1,
  down = 2,
  left = 3,
  right = 4,
  upLeft = 5,
  upRight = 6,
  downLeft = 7,
  downRight = 8,
  zoomIn = 9,
  zoomOut = 10,
  focusIn = 11,
  focusOut = 12,
  irisOpen = 13,
  irisClose = 14
};

export interface Media {
  id: number,
  start: Date,
  end: Date
}

@Component({
  selector: 'lib-webrtc-device-view',
  templateUrl: './webrtc-device-view.component.html',
  styleUrls: ['./webrtc-device-view.component.css']
})
export class WebrtcDeviceViewComponent implements OnInit, OnDestroy, VaelsysWebrtcComponent {
  callErrors : Subject<CallError>;

  // Private props
  private _connectionStateSubscription : Subscription | undefined;
  private _lastError: CallError | undefined;
  private _deviceId: number | undefined;
  private _deviceServiceId: number | undefined;
  private _call: any;
  private _shouldStartMuted : boolean = false;
  private _finishedWithoutErrors : boolean = false;
  // Under some circumstances we want to wait before ending a call. 
  // For example, the WebRTC closed event might be received just a few ms earlier that the CallFinished wamp event
  // Fot those cases, we use this timeout
  private _endTimeout : NodeJS.Timeout | undefined;
  private _endPromise : Promise<boolean> | undefined;
  // Used to make sure if a call is being started we wait before starting the next one. Otherwise, multiple calls could be 
  // started at the same time and some of them will never be finished
  private _startPromise : Promise<boolean> | undefined;

  private _allowPause : boolean = false;
  private _streamStateChangeTimeout : NodeJS.Timeout | undefined;

  constructor(@Inject(LibConfigService) private config: LibConfig, private service: VaelsysWebrtcLibService) {
    this.callErrors = new Subject<CallError>;
  }

  ngOnInit() {
    this.service.attachComponent(this);
  }

  ngOnDestroy() {
    this.cleanup();
    this.service.detachComponent(this);
  }

  @HostListener('window:beforeunload', [ '$event' ])
  async beforeUnloadHandler() {
    // Make sure the call is closed
    if (this._call && this._call.callId) {
      console.log('Ending call [before unload handler]');
      await this._assertCallFinished();
    }
  }

  async select(deal_id: string, s_ins: number, s_cam: number, device_id: string, validation_code: string) : Promise<boolean> {
    let ret: boolean = false;

    this.cleanup();

    this._lastError = undefined;
    this._connectionStateSubscription = this.service.connectionState.subscribe(state => {
      // If the wamp connection fails and there's an active call, close the call
      if (state == 'disconnected' && this._call && this._call.callId) {
        this._assertCallFinished(false);
      }
    });

    // Fetch device info, which will call the check method if necessary
    const deviceInfo = await this.service.deviceInfo(deal_id, s_ins, s_cam, device_id, validation_code);
    if (!deviceInfo) {
      this._lastError = this.service.lastError();
      return ret;
    }
    
    this._deviceId = deviceInfo.id;
    for (let s in deviceInfo.services) {
      let service = deviceInfo.services[s] as any;
      if (service.type == 'MediaInput') {
        this._deviceServiceId = service._id;
        break;
      }
    }

    if (!this._deviceId || !this._deviceServiceId) {
      console.error('Failed to calculate device service id', device_id, this._deviceId, this._deviceServiceId, deviceInfo);
      return ret;
    }
    
    ret = true;
    return ret;
  }

  private _setState(state: CallConnectionState) {
    if (this._streamStateChangeTimeout) {
      clearTimeout(this._streamStateChangeTimeout);
      this._streamStateChangeTimeout = undefined;
      if (state != 'streamdisconnected' && state != 'failed') {
        console.log('state changed timeout cancelled. New state is', state);
      }
    }

    // If the state is closed, make sure the call is closed too
    if ((state == 'streamdisconnected' || state == 'closed') && this._call && this._call.callId) {
      console.log('Ending call [state changed to ' + state + ']');
      // Wait 100 ms in case the call has been finished in the server
      this._assertCallFinished(true, 100);
    } 
    
    // Notify the service about the new connection state
    this.service.setCallState(state);
  }

  async cleanup() : Promise<boolean> {
    let ret: boolean = false;
    
    this._lastError = undefined;

    if (this._streamStateChangeTimeout) {
      clearTimeout(this._streamStateChangeTimeout);
      this._streamStateChangeTimeout = undefined;
    }

    if (this._endTimeout) {
      clearTimeout(this._endTimeout);
      this._endTimeout = undefined;
    }

    this._connectionStateSubscription?.unsubscribe();
    this._connectionStateSubscription = undefined;

    if (this._call && this._call.callId) {
      console.log('Ending call [disconnect called]');
      await this._assertCallFinished();
    }

    const connectionState = this.service.connectionState.value;

    if (connectionState ==  'streamconnecting' 
      || connectionState == 'streamconnected' 
      || connectionState == 'streaming'
      || connectionState == 'streamdisconnected' ) {
        this._setState('closed');
      }

    ret = true;
    return ret;
  }

  lastError() : CallError | undefined {
    return this._lastError;
  }

  async ptzControl(command: PTZCommand, speed: number) : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    await wamp_call('ptzControl', {
      deviceServiceId: '' + this._deviceServiceId, 
      command: command,
      speed: speed
    }).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        ret = true;
        this._lastError = undefined;
      }
    }).catch((error: any) => {
        console.error("RPC call failed", error);
        this._lastError = {
          errorCode: 'Wamp_'+error.message,
          errorString: 'Failed to control PTZ'
        };
    });

    return ret;
  }

  async setAutoTracking(enabled: boolean) : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    await wamp_call('setAutotrackingEnabled', {
      deviceServiceId: '' + this._deviceServiceId, 
      enabled: enabled
    }).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        ret = true;
        this._lastError = undefined;
      }
    }).catch((error: any) => {
        console.error("RPC call failed", error);
        this._lastError = {
          errorCode: 'Wamp_'+error.message,
          errorString: 'Failed to control autotracking'
        };
    });

    return ret;
  }

  async isAutoTrackingEnabled() : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    await wamp_call('isAutotrackingEnabled', {
      deviceServiceId: '' + this._deviceServiceId,
    }).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        ret = res.argsDict.enabled;
        this._lastError = undefined;
      }
    }).catch((error: any) => {
        console.error("RPC call failed", error);
        this._lastError = {
          errorCode: 'Wamp_'+error.message,
          errorString: 'Failed to read autotracking'
        };
    });

    return ret;
  }

  async setLight(enabled: boolean) : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    await wamp_call('setOutputEnabled', {
      deviceServiceId: '' + this._deviceServiceId, 
      enabled: enabled
    }).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        ret = true;
        this._lastError = undefined;
      }
    }).catch((error: any) => {
      this._lastError = {
        errorCode: 'Wamp_'+error.message,
        errorString: 'Failed to control light'
      };
    });

    return ret;
  }

  async isLightEnabled() : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    await wamp_call('isOutputEnabled', {
      deviceServiceId: '' + this._deviceServiceId, 
    }).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        ret = res.argsDict.enabled;
        this._lastError = undefined;
      }
    }).catch((error: any) => {
      this._lastError = {
        errorCode: 'Wamp_'+error.message,
        errorString: 'Failed to read light'
      };
    });

    return ret;
  }

  async setPaused(paused: boolean) : Promise<boolean> {
    let ret : boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    if (!this._allowPause) {
      console.warn('Pause not allowed');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Pause/Resume not allowed'
      };

      return ret;
    }

    if (!this._call || !this._call.callId) {
      console.error('Call not started');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Call not started'
      };
      return ret;
    }

    ret = await this._call.setPaused(paused);
    if (!ret) {
      console.error('Failed to set call paused', paused);
      this._lastError = {
        errorCode: this._call?.lastErrorCode,
        errorString: this._call?.lastErrorString
      };
    }

    return ret;
  }

  async isPaused() : Promise<boolean> {
    let ret : boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    if (!this._allowPause) {
      console.warn('Pause not allowed');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Pause not allowed'
      };

      return ret;
    }

    if (!this._call || !this._call.callId) {
      console.error('Call not started');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Call not started'
      };
      return ret;
    }

    ret = await this._call.isPaused();
    return ret;
  }

  async setAudioSending(enabled: boolean) : Promise<boolean> {
    let ret: boolean = false;

    if (!(await this._isServiceConnected())) {
      return false;
    }

    if ((this.service.connectionState.getValue() != 'streamconnected' && this.service.connectionState.getValue() != 'streaming') || !this._call) {
      console.error('Call not started');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Call not started'
      };
      return ret;
    }

    ret = await this._call.setEnabledStream('audio-in', enabled);
    if (!ret) {
      console.error('Failed to control audio sending');
      this._lastError = {
        errorCode: this._call.lastErrorCode,
        errorString: this._call.lastErrorString
      };
    }

    return ret;
  }

  isAudioSending() : boolean {
    let ret: boolean = false;

    if (this._call) {
      ret = this._call.isEnabledStream('audio-in');
    } else {
      console.error('Call not started');
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Call not started'
      };
    }

    return ret;
  }

  async setMuted(muted: boolean) : Promise<boolean> {
    let ret: boolean = false;

    let prevShouldStartMuted = this._shouldStartMuted;
    this._shouldStartMuted = muted;

    if (this._call) {
      ret = await this._call.setMutedAudio(muted);
      if (!ret) {
        this._shouldStartMuted = prevShouldStartMuted;
        this._lastError = {
          errorCode: this._call.lastErrorCode,
          errorString: this._call.lastErrorString
        };
      }
    } else {
      return true;
    }


    return ret;
  }

  isMuted() : boolean {
    let ret: boolean = false;

    if (this._call) {
      ret = this._call.isMutedAudio();
    } else {
      ret = this._shouldStartMuted;
    }

    return ret;
  }

  async mediaLookup(start: Date, end: Date, timeout?: number) : Promise<Media[]>{
    let ret : Media[] = [];

    if (!(await this._isServiceConnected())) {
      return ret;
    }

    await wamp_call('mediaLookup', {
      mediaInputId: '' + this._deviceServiceId, 
      start: start.toISOString(),
      end: end.toISOString()
    }, timeout).then((res: any) => {
      if (!res.argsDict.status) {
        console.error("Invalid response: " + res.argsDict.errorDesc);
        this._lastError = {
          errorCode: res.argsDict.errorCode ?? 'InvalidResponse',
          errorString: res.argsDict.errorDesc
        };
      } else {
        this._lastError = undefined;
        let results = res.argsDict.results;
        for (var i = 0; i < results.length; i++) {
          let startDate = new Date(Date.parse(results[i].start));
          let endDate = new Date(Date.parse(results[i].end));

          // Check that the dates are valid
          if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
            let aux : Media = {
              id: results[i].id,
              start: startDate,
              end: endDate,
            };
  
            ret.push(aux);
          } else {
            console.error('Invalid media', results[i]);
          }
        }
      }
    }).catch((error: any) => {
      this._lastError = {
        errorCode: 'Wamp_'+error.message,
        errorString: 'Failed to perform lookup'
      };
    });

    return ret;
  }

  async live(requireSendingAudio : boolean = true) : Promise<boolean> {
    this._allowPause = false;
    return this._startCall(undefined, undefined, requireSendingAudio);
  }

  async seek(date: Date, endDate?: Date) : Promise<boolean> {
    this._allowPause = true;
    return this._startCall(date, endDate);
  }

  async stop() : Promise<boolean> {
    let ret : boolean = false;

    if (this._call && this._call.callId) {
      console.log('Ending call [stop called]');
      ret = await this._assertCallFinished();
      this._setState('closed');
    } else {
      console.warn('No call to end');
    }

    return ret;
  }

  private async _isServiceConnected() : Promise<boolean> {
    const ret = await this.service.assertIsConnected();

    if (!ret) {
      this._lastError = {
        errorCode: 'InvalidCall',
        errorString: 'Service not connected'
      }
    }

    return ret;
  }

  _showLoadingSpinner() : boolean {
    return this.service.connectionState.value != 'streaming';
  }

  private async _startCall(seek?: Date, seekEnd?: Date, requireSendingAudio?: boolean) : Promise<boolean> {
    let ret : boolean = false;

    if (!seek && seekEnd) {
      this._lastError = {
        errorCode: 'BadArguments',
        errorString: "Can't send end without start seek date"
      }
      return false;
    }

    if (!(await this._isServiceConnected())) {
      return false;
    }

    this._lastError = undefined;
    this._finishedWithoutErrors = false;
    const state = this.service.connectionState.value;
    const valid = state == 'connected' || state == 'streamconnecting' || state == 'streamconnected' || state == 'streaming' || state == 'streamdisconnected';
    if (!valid) {
      this._lastError = {
        errorCode: 'BadCall',
        errorString: 'Invalid state'
      }
      return ret;
    }

    if (this._startPromise) {
      await this._startPromise;
    }

    this._startPromise = this._startCallPromise(seek, seekEnd, requireSendingAudio);
    ret = await this._startPromise!;
    this._startPromise = undefined;

    return ret;
  }

  private async _startCallPromise(seek?: Date, seekEnd?: Date, requireSendingAudio?: boolean) : Promise<boolean> {
    let ret : boolean = false;

    if (this._call && this._call.callId) {
      console.log('Ending call [starting new call]');
      await this._assertCallFinished();
    }

    this._call = new DeviceWebRtcCall(
      'webrtc-view',
      ''+this._deviceId,
      ''+this._deviceServiceId,
      (event: any) => {
        console.log(event);
        let finishError = event.properties.code == 'IncommingCallFinished' || event.properties.code == 'IncommingCallFinish';
        if (event.properties.errorString) {
          let error : CallError = {
            errorCode: event.properties.errorCode ?? 'Unknown',
            errorString: event.properties.errorString
          };
          

          // There has been an error on the call
           
          if (finishError) {
            this._lastError = error;
            this._setState('failed');

            if (this._call && this._call.callId) {
              console.log('Ending call [received ' + event.properties.code + ']');
              this._assertCallFinished(false);
            }
          }

          this.callErrors.next(error);
        } else if (finishError) {
          // The call has been finished without errors
          this._finishedWithoutErrors = true;
          this._setState('closed');
          if (this._call && this._call.callId) {
            console.log('Ending call [Finished without errors: ' + event.properties.code + ']');
            this._assertCallFinished(false);
          }
        }
      },
      (connectionState: string) => {
        console.log('WebRTC conn state is: ', connectionState)
        if (connectionState == 'new') {
          //this._setState(connectionState);
        } else if (connectionState == 'connecting') {
          this._setState('streamconnecting');
        } else if (connectionState == 'connected') {
          this._setState('streamconnected');
          // Of the webrtc connection is stablished, check the video player state
          const videoElements = document.getElementsByTagName('video');
          if (videoElements.length == 0 || !videoElements[0]) {
            console.error('No video element to check for playing');
          } else {
            const video = videoElements[0];
            let playingEvents = 'playing';
            playingEvents.split(' ').forEach(t => video.addEventListener(t, (e) => {
              // console.log('Play event: ' + e.type + ' - ' + this.connectionState.value);
              if (this.service.connectionState.value == 'streamconnected') {
                this._setState('streaming');
              }
            }));

            let stoppedEvents = 'suspend pause';
            stoppedEvents.split(' ').forEach(t => video.addEventListener(t, (e) => {
              // console.log('Pause event: ' + e.type + ' - ' + this.connectionState.value);
              if (this.service.connectionState.value == 'streaming') {
                this._setState('streamconnected');
              }
            }));
          }
        } else if (connectionState == 'disconnected') {
          // The state may change to disconnected for a few milliseconds/seconds and then change to connected again
          // using some other ICE transport, so we use a timeout
          if (!this._streamStateChangeTimeout) {
            // If the call has finished, we don't need the timeout
            if (this._call && !this._finishedWithoutErrors && !this._endPromise) {
              this._streamStateChangeTimeout = setTimeout(() => {
                console.log('streamdisconnected timeout');
                this._setState('streamdisconnected');
              }, 5*1000);
            } else {
              this._setState('streamdisconnected');
            }
          }
        } else if (connectionState == 'failed') {
          if (this._call && !this._finishedWithoutErrors && !this._endPromise) {
            if (!this._streamStateChangeTimeout) {
              this._streamStateChangeTimeout = setTimeout(() => {
                console.log('failed timeout');
                this._lastError = {
                  errorCode: 'Connection',
                  errorString: 'WebRTC connection failed'
                }
    
                this.callErrors.next(this._lastError);
                this._setState('failed');
              }, 1*1000);
            }
          } else {
            this._setState('closed');
          }
        } else if (connectionState == 'closed') {
          this._setState('closed');
        }
      },
      this.config.iceServers,
      this.config.iceServers ? "relay" : null
    );

    ret = await this._call.startCall({
      video: 'recv',
      audio: seek ? 'recv' : 'sendrecv',
      audio_enabled: 'recv',
      ... seek && {seek: seek?.toISOString()},
      ... seekEnd && {seekEnd: seekEnd?.toISOString()},
      video_required: 'recv',
      audio_required: seek ? 'recv' : requireSendingAudio ? 'sendrecv' : 'recv',
      audio_muted: this._shouldStartMuted,
      heartbeat: true,
      heartbeatTicksUserActivity: true
    });

    if (!ret) {
      this._lastError = {
        errorCode: this._call.lastErrorCode,
        errorString: this._call.lastErrorString
      };
      console.error(this._lastError);
    }

    return ret;
  }

  private async _assertCallFinished(hangup:boolean=true, timeout:number=0) : Promise<boolean> {
    let ret = false;

    if (timeout > 0) {
      if (!this._endTimeout) {
        this._endTimeout = setTimeout(() => {
          this._assertCallFinished(hangup);
        }, timeout);
      }

      return true;
    }

    if (this._endTimeout) {
      clearTimeout(this._endTimeout);
      this._endTimeout = undefined;
    }

    if (this._endPromise) {
      if (hangup)  {
        console.warn('Already ending. Wait');
      }
      ret = await this._endPromise;
    } else {
      this._endPromise = this._call.endCall(hangup);
      ret = await this._endPromise!;
      this._call = undefined;
      this._endPromise = undefined;
    }

    if (this.service.connectionState.value == 'streaming') {
      //console.warn('State is still streaming. Changing it');
      this._setState('streamconnected');
    }

    return ret;
  }
}
